import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridClasses } from "@mui/x-data-grid";
import { ImSpinner8 } from "react-icons/im";
import DisplayAmount from "../../../../views/admin/shared/DisplayAmount";

type CommissionRow = {
  asset: string;
  available: string;
  pending: string;
  crypto: boolean;
  thisMonth: string;
  lastMonth: string;
};

type Props = {
  data: CommissionRow[];
  isFetchingCommission: boolean;
};

const AgentCommissionTab = ({ data, isFetchingCommission }: Props) => {
  const [fiatData, setFiatData] = useState<CommissionRow[]>([]);
  const [cryptoData, setCryptoData] = useState<CommissionRow[]>([]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const fiat = data.filter((item: CommissionRow) => !item.crypto);
      const crypto = data.filter((item: CommissionRow) => item.crypto);

      setFiatData(fiat);
      setCryptoData(crypto);
    }
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) =>
        params.row.asset,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) => (
        <DisplayAmount
          amount={Number(params.row.available)}
          currency={params.row.asset}
        />
      ),
    },
    {
      field: "thisMonth",
      headerName: "This Month",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) => (
        <DisplayAmount
          amount={Number(params.row.thisMonth)}
          currency={params.row.asset}
        />
      ),
    },
    {
      field: "lastMonth",
      headerName: "Last Month",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) => (
        <DisplayAmount
          amount={Number(params.row.lastMonth)}
          currency={params.row.asset}
        />
      ),
    },
  ];

  const columnsCrypto: GridColDef[] = [
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) =>
        params.row.asset,
    },
    {
      field: "available",
      headerName: "Available",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) =>
        params.row.available,
    },
    {
      field: "thisMonth",
      headerName: "This Month",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) =>
        params.row.thisMonth,
    },
    {
      field: "lastMonth",
      headerName: "Last Month",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<CommissionRow>) =>
        params.row.lastMonth,
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start text-md",
    row: "",
  };

  const dataGridSx = {
    "& .MuiDataGrid-cell": {
      padding: "8px 16px",
    },
    "& .MuiDataGrid-columnHeader": {
      padding: "8px 16px",
    },
    "& .MuiTablePagination-input": {
      marginRight: "-20px",
    },
    "& .MuiTablePagination-displayedRows": {
      display: "none",
    },
  };

  if (isFetchingCommission || !data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-7">
        <ImSpinner8
          className="spinner mt-1 text-primary-500"
          style={{ fontSize: "1.8rem" }}
        />
      </div>
    );
  }

  if (
    !isFetchingCommission &&
    (!fiatData || fiatData.length === 0) &&
    (!cryptoData || cryptoData.length === 0)
  ) {
    return (
      <div className="p-7">
        <p className="py-8 text-center text-sm text-gray-500">
          No commission earned
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-8">
      {fiatData && fiatData.length > 0 && (
        <Box mb={4}>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Fiat Commission
          </div>
          <DataGrid
            sx={dataGridSx}
            loading={false}
            rows={fiatData}
            columns={columns}
            getRowId={(row) => row.asset}
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={true}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>
      )}
      {cryptoData && cryptoData.length > 0 && (
        <Box>
          <div className="mb-2 text-sm font-semibold text-navy-700">
            Wallet Commission
          </div>
          <DataGrid
            sx={dataGridSx}
            loading={false}
            rows={cryptoData}
            columns={columnsCrypto}
            getRowId={(row) => row.asset}
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={true}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default AgentCommissionTab;
